import type HttpMethod from './modules/api/types/HttpMethod';
import type Interceptor from './modules/api/types/Interceptor';
import type InterceptorRequestData from './modules/api/types/InterceptorRequestData';
import type Response from './modules/api/types/Response';
import type ResponseBody from './modules/api/types/ResponseBody';
import type Request from './modules/api/types/RequestInterface';
import type ParentEntity from './modules/api/types/ParentEntity';
import type { OpportunityType } from './modules/helpers/modules/job-postings/types';

export * from './modules/helpers/modules/tracking/types';
export * from './modules/helpers/modules/job-postings/types';
export * from './modules/helpers/modules/job-applications/types';
export * from './modules/helpers/modules/languages/types';
export * from './modules/helpers/modules/candidate/types';
export * from './modules/helpers/modules/employer/types/Employer';
export * from './modules/helpers/modules/recruiters/types';

export type { InterceptorRequestData };

export interface Entity {
  id?: number | string;
  [others: string]: any;
}

export interface ApiResponse<EntityType = Entity> {
  body: EntityType;
  pagination?: { totalElements?: number };
  links?: any;
}

export type HelperResult<EntityType = Entity> = Promise<ApiResponse<EntityType>>;

export type ClientLanguage = 'en' | 'fr';
export type ApiVersion = `${number}.${number}`;

export interface ClientApiOptions {
  language?: ClientLanguage;
  rootUrl?: string;
  version?: ApiVersion;
}

export interface ClientApiInterface<Helpers extends Record<string, unknown> = {}> {
  helpers: Helpers;
  rootUrl: string;
  requestInterceptors: Interceptor<InterceptorRequestData>[];
  responseInterceptors: Interceptor<Response>[];
  accessToken: string | null;
  createRequest<EntityType extends ResponseBody | void>(
    path: string,
    method: HttpMethod
  ): Request<EntityType>;
  createGetRequest<EntityType extends ResponseBody | void>(path: string): Request<EntityType>;
  createPatchRequest<EntityType extends ResponseBody | void>(path: string): Request<EntityType>;
  createPostRequest<EntityType extends ResponseBody | void>(path: string): Request<EntityType>;
  createPutRequest<EntityType extends ResponseBody | void>(path: string): Request<EntityType>;
  createDeleteRequest(path: string): Request<void>;
  addRequestInterceptor(
    interceptor: Interceptor<InterceptorRequestData>,
    prependInterceptor: boolean
  ): this;
  addResponseInterceptor(interceptor: Interceptor<Response>): this;
  getDynamicHelper(entityName: string, parentEntityName?: ParentEntity | ParentEntity[]): unknown;
}

export interface PathCategory {
  color: string;
  id: number;
  name: string;
}

export interface PathPrerequisiteCertificate {
  level: string;
  field: string;
}

export type CertificationCategory = 'job-certification' | 'skill-certification';

interface CertificationHolder {
  id: number;
  name: string;
  logo: string;
}

export interface PathCertification {
  id: number;
  authority: string;
  title: string;
  type: string;
  level: string;
  prerequisiteCertificate: PathPrerequisiteCertificate;
  europeanLevel: number;
  frenchLevel: string;
  holders: CertificationHolder[];
  ministerialOrderPublishedDate: Date;
  NSFCode: string;
  publicationDateInTheOfficialRegister: Date;
  RNCPFileUrl: string;
  ROMECodes: string[];
  marketingLabel: string;
  category: CertificationCategory;
}

export interface PathProject {
  OpenClassroomsUrl: string;
  createdAt: Date;
  duration: string;
  evaluatorType: string;
  id: number;
  illustration?: any;
  isActive: boolean;
  isAvailable: boolean;
  isStudentDeliverable: boolean;
  language: string;
  lifecycleStatus: string;
  requiredPresentation: boolean;
  shortDescription: string;
  status: string;
  title: string;
  updatedAt: Date;
  url: string;
}

export interface PathFundingMechanism {
  country: string;
  eligibleCountries: string[];
  id: number;
  name: string;
  shortName: string;
  type: string;
}

export interface Path {
  category: PathCategory;
  certifications: PathCertification[];
  description: string;
  duration: string;
  format: PathFormat;
  id: number;
  illustration: string;
  language: 'en' | 'fr';
  OpenClassroomsUrl: string;
  openedAt: Date;
  projectsDuration: string;
  projectsTotalDuration: string;
  projectsSupervisedDuration: string;
  projectsGuidedDuration: string;
  releaseSeason?: any;
  releaseYear?: any;
  shortDescription: string;
  status: string;
  title: string;
  longDescription: string;
  projects: PathProject[];
  fundingMechanisms: PathFundingMechanism[];
}

export interface UserPath extends Path {
  completion: Completion;
  userLearningInformation: LearningInformation;
  userCanReEnroll: boolean;
}

interface Completion {
  actual: number;
  expected: number;
  status: string;
  target: number;
}

export interface Course {
  OpenClassroomsUrl: string;
  category: {
    color: string;
    name: string;
  };
  categoryColor: string;
  categoryId: number;
  description: string;
  difficulty: string;
  displayDifficulty: string;
  duration: string;
  hasAcademicCredits: boolean;
  id: number;
  illustration: string;
  isActive: boolean;
  isCertifying: boolean;
  isPreregistrable: boolean;
  isSinglePage: boolean;
  isVideoBased: boolean;
  language: string;
  license: string;
  openedAt: string;
  publishedAt: string;
  prerequisite: null;
  shortDescription: string;
  slug: string;
  status: string;
  teaser: string;
  teaserUrl: string;
  title: string;
  updatedAt: string;
  videoBased: boolean;
  courseFollowedAt: string;
  lastCourseActivityDate: string;
  learningActivityStatus: string;
  userCourseCertificateIsValid: boolean;
  userCourseCompletionRate: number;
  userCourseScore: null;
  userHasFollowedCourse: boolean;
  userIsCertified: boolean;
  userIsFollowingCourse: boolean;
}

export type CourseStatus = 'draft' | 'waiting-for-publication' | 'published';

interface LearningInformation {
  completedAt: string;
  isFollowingLearningPath: boolean;
  unfollowedAt: string;
}

export type PathFormat =
  | 'skill-program'
  | 'path'
  | 'course-collection'
  | 'competency'
  | 'vae'
  | 'coaching'
  | 'internal';

export enum StudentApplicationStages {
  Draft = 'draft',
  Submitted = 'submitted',
  Admitted = 'admitted',
  Disqualified = 'disqualified',
  RecommendationSent = 'recommendation-sent',
}

export interface StudentApplicationPath {
  category: PathCategory;
  certifications: Array<{
    certificationPrerequisite: PathPrerequisiteCertificate;
    marketingLabel: string;
  }>;
  id: number;
  title: string;
  locale: string;
  slug: string;
  illustration: string;
  duration: string;
}

export type FundingType =
  | 'apprenticeship'
  | 'publicFunding'
  | 'cpf'
  | 'ptp'
  | 'companyFunding'
  | 'selfpaid'
  | 'poei';

export type AllFundingTypes = FundingType | 'other' | 'dontKnow';

export type GrantedProgram =
  | 'apprenticeship'
  | 'cpf'
  | 'selfpaid'
  | 'us-apprenticeship'
  | 'idfelearning'
  | 'pole-emploi-foad'
  | 'btob'
  | 'transition-pro'
  | 'individual-funding';

export type ContractType = 'apprenticeship' | 'contrat-pro' | undefined;

export interface StudentApplication {
  appsource: string;
  createdAt: string;
  data: Record<string, Record<string, any>>;
  fundingTypes: AllFundingTypes[];
  grantedPath: {
    category: PathCategory;
    id: number;
    locale: string;
    slug: string;
    title: string;
  };
  grantedProgram: GrantedProgram;
  apprenticeshipContractTypes: ContractType[];
  id: string;
  lastStageEventAt: string;
  pathId?: number;
  paths: StudentApplicationPath[];
  pathSetAtCreation: boolean;
  stage: StudentApplicationStages;
  user: {
    id: number;
    displayableName: string;
    firstName: string;
    lastName: string;
  };
  wishedStartDate: string;
  companyFound?: 'yes' | 'no' | 'ongoing';
}

export interface StudentApplicationCreationValues {
  pathId?: number;
  appsource: string;
  data: Record<string, unknown>;
  fundingTypes?: FundingType[];
}

export interface StudentApplicationMutationValues {
  pathIds?: number[];
  data?: Record<string, unknown>;
  fundingTypes?: FundingType[];
  stepName?: string;
  wishedStartDate?: string;
  fundingRequest?: {
    fundingBodyFileId: string;
    type: 'cpf';
  };
  companyFound?: 'yes' | 'no' | 'ongoing';
}

export interface StudentStage {
  studentStage: 'member' | 'prospect' | 'applicant' | 'candidate' | 'enrollee' | 'student';
}

export interface StudentApplicationOffer {
  program: GrantedProgram;
  status: 'pending' | 'selected';
  type: 'recommendation';
  path: StudentApplicationPath;
}

export interface StudentApplicationEvent {
  name: string;
  createdAt: Date;
  eventData: Record<string, any>;
}

export interface CertificationLevel {
  id: number;
  level:
    | 'n/a (Level 0)'
    | 'Level 1'
    | 'Level 2'
    | 'Level 3'
    | 'Level 4'
    | 'Level 5'
    | 'Level 6'
    | 'Level 7'
    | 'Level 8';
}

export interface TrainingCenter {
  name: string;
  city: string;
}

export interface TrainingMajor {
  id: number;
  name: string;
  rootName: string;
  externalId: string;
}

export interface VerifiablePersonalDocument {
  id: string;
  filename: string;
  size: number;
  type: string;
  verificationStatus: 'pending' | 'verified' | 'rejected';
  temporaryUrl?: string;
  rejectionReason?: 'uncertified document' | 'incorrect document kind' | 'infected file' | 'other';
  comment?: string;
}

export interface TrainingActivityProofFile extends VerifiablePersonalDocument {
  kind: 'diploma' | null;
}

export interface TrainingActivity {
  id: number;
  certificationLevel: CertificationLevel;
  certificationLevelId?: CertificationLevel['id'];
  customCertificationLevel?: string | null;
  description?: string;
  diplomaUrl?: string;
  duration?: string;
  endedAt?: string;
  name?: string | null;
  ocRelatedActivity?: string;
  ocRelatedSkills?: string;
  startedAt?: string | null;
  trainingCenter?: TrainingCenter | null;
  verificationStatus: 'verified' | 'pending';
  trainingType: string;
  certificationRanking: 'highest' | 'latest' | 'highest-latest' | 'other';
  trainingMajor: TrainingMajor | null;
  trainingMajors: TrainingMajor[];
  graduatedAt: string | null;
}

export interface TrainingActivityMutation {
  name?: string | null;
  ocRelatedActivity?: string;
  ocRelatedSkills?: string;
  startedAt?: string | null;
  endedAt?: string;
  description?: string;
  diplomaUrl?: string;
  certificationLevelId?: CertificationLevel['id'];
  certificationLevel: string | null;
  customCertificationLevel?: string | null;
  trainingType: string;
  trainingCenter?: TrainingCenter | null;
  id?: number;
}

export interface ResumeFile {
  id: string;
  filename: string;
  size: number;
  type: string;
  temporaryUrl?: string;
}

interface AdministrativeArea {
  isoCode?: string;
  name: string;
}

export type AddressType = 'home' | 'billing';

type Geocordinates = {
  latitude: number;
  longitude: number;
};

type ProviderInformation = {
  name: 'Mapbox' | 'Google Place';
  addressId?: string;
  cityId?: string;
  districtId?: string;
};
export interface Place {
  administrativeArea: AdministrativeArea | null;
  city: string;
  country: string;
  formattedAddress: string;
  postalCode: string;
  geocoordinates?: Geocordinates;
  providerInformation?: ProviderInformation;
  additionalAddressInformation?: string;
}

export interface Address {
  id: number;
  type: AddressType;
  updatedAt: string;
  place: Place;
}

export interface AddressMutation {
  formattedAddress: string;
  city: string;
  postalCode: string;
  administrativeArea: AdministrativeArea;
  country: string;
  geocoordinates?: Geocordinates;
  providerInformation?: ProviderInformation;
  additionalAddressInformation?: string;
}

export interface PhoneNumber {
  phoneNumber: string;
}

export interface FieldOfExperiences {
  id: number;
  name: string;
  externalId: string;
  rootName: string;
}

export interface ProfessionalStatusMutationBody {
  status:
    | 'employee'
    | 'civil-servant'
    | 'self-employed'
    | 'student'
    | 'job-seeker'
    | 'not-working'
    | 'active-military'
    | 'retired';
  professionalActivityId?: number;
  jobLayOffPlan: 'lay-off-plan' | null;
}

export type ProfessionalStatusValue =
  | 'employee'
  | 'civil-servant'
  | 'self-employed'
  | 'student'
  | 'job-seeker'
  | 'not-working'
  | 'active-military'
  | 'retired';

export interface ProfessionalStatus {
  id: number;
  user: {
    publicId: number;
    id: number;
  };
  status: ProfessionalStatusValue;
  professionalActivity?: { id: number } | null;
  createdAt: string;
  jobLayOffPlan: 'lay-off-plan' | null;
}

export type JobSeekerProofFile = VerifiablePersonalDocument;

export type JobSeekerProofFileMutationBody = {
  filename: string;
  contents: string | ArrayBuffer | null;
};

export type UserContactCategories = 'job-seeker-advisor' | 'employer';

export interface UserContact {
  id: string;
  category: UserContactCategories;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  countryIso2Code?: string | null;
  languageIso2Code?: string | null;
}

export interface Milestone {
  id: number;
  title: string;
  content: string;
}

export interface ActivationDatafieldsToComplete {
  missingDatafields: Array<'funding-request-file-id' | 'highest-certification-proof'>;
}

export interface SpecificStatuses {
  id: number;
  code: SpecificStatusCodes;
  description: string;
  subdescription: string;
}

export interface UserSpecificStatus {
  id: number;
  specificStatus: {
    code: SpecificStatusCodes;
  };
  value: number;
  createdAt: string;
  updatedAt: string;
}

export type SpecificStatusCodes =
  | 'DISABLED_WORKER'
  | 'ATHLETE'
  | 'REFUGEE'
  | 'MIGRANT'
  | 'VETERAN'
  | 'HOMELESS_OR_RUNAWAY'
  | 'PRISON_OR_JAIL_INMATE'
  | 'ASYLUM_SEEKER'
  | 'DISPLACED_HOMEMAKER'
  | 'DEFICIENT_IN_BASIC_SKILLS'
  | 'PREGNANT_OR_PARENTING_AT_A_YOUNG_AGE'
  | 'FOSTER_CHILD';

export interface TimeAvailability {
  hoursPerWeek: number;
}

export interface TrainingAccommodation {
  code: TrainingAccommodationCodes;
  description: string;
  subdescription: string;
}

export type TrainingAccommodationCodes =
  | 'TRAINING_TIME_ADJUSTMENT'
  | 'MENTORSHIP_SESSION_ARRANGEMENT'
  | 'PRESENTATION_ADJUSTMENT'
  | 'GUIDANCE_BY_A_SPECIALIZED_MENTOR'
  | 'ADAPTED_EQUIPMENT'
  | 'ADAPTED_WORK_ENVIRONMENT'
  | 'OTHER';

export interface TrainingPlanItem {
  externalId: string;
  id: number;
  type: string;
  position: number;
  learningPathId: number;
  startedAt: string;
  endedAt: string;
  title: string;
  status: 'ongoing' | 'upcoming' | 'completed' | 'expired';
}

export interface TrainingPlan {
  id: number;
  userId: number;
  ulid: string;
  studentApplicationId: string;
  startedAt: string;
  endedAt: string;
  items: TrainingPlanItem[];
  title: string;
}

export interface FinancialAid {
  amount: number;
  currencyIsoCode: string;
  backers: string[];
  campaign: { id: number };
  customPathDuration: number;
  expectedPathCompletion: number;
  fileNumber: string;
  invoiceNumber: string;
  signedDate: string;
  submissionDate: string;
}

export interface TrainingPeriod {
  ulid: string;
  endedAt: string;
  learningPathId: number;
  learningPaths: Array<{ learningPathId: number; position: number }>;
  metadata: {
    financialAid: FinancialAid;
    source: string;
    sourceId: string;
    sourceStatus: string;
  };
  startedAt: string;
  status: 'activated' | string;
  studentApplicationId: string;
}

export interface UserDataCode {
  criteria: Array<{
    datafield: string;
    description: string;
    expression: string;
    options: string[];
    result: boolean;
  }>;
  options: string[];
  result: boolean;
}

export interface TrainingProgramRecommendation {
  trainingProgram: {
    category: PathCategory;
    id: number;
    name: string;
    illustration: string;
    certifications: PathCertification[];
    duration: string;
  };
  status: 'eligible' | 'not-eligible';
  score: number;
  options: { derogatory_process: boolean; vap: boolean };
  reason: {
    hint: string;
    userDataCodes: Array<UserDataCode>;
  };
}

export type FundingProgramSlug =
  | 'contrat-apprentissage'
  | 'us-apprenticeship'
  | 'cpf'
  | 'selfpaid'
  | 'pole-emploi-foad'
  | 'idfelearning'
  | 'financement-entreprise'
  | 'contrat-pro'
  | 'poei'
  | 'individual-funding'
  | 'transition-pro'
  | 'PACA'
  | 'auvergne-rhone-alpes'
  | 'financement-entreprise-pse';

export interface FundingProgramRecommendation {
  fundingProgram: {
    id: string;
    name: string;
    slug: FundingProgramSlug;
    description: string;
  };
  status: 'eligible' | 'not-eligible';
  score: number;
}

export interface Allowances {
  id: number;
  code: AllowanceCodes;
  description: string;
  subdescription: string;
  countries?: AllowancesCountries[];
}

export type AllowanceCodes =
  | 'LOW_INCOME'
  | 'LOW_INCOME_ROTW'
  | 'AAH_ALLOCATIONS_AUX_ADULTES_HANDICAPES'
  | 'ASP_ALLOCATION_SECURITE_PROFESSIONNELLE'
  | 'ASS_ALLOCATION_DE_SOLIDARITE_SPECIFIQUE'
  | 'API_ALLOCATION_PARENT_ISOLE'
  | 'ARE_ALLOCATION_RETOUR_A_EMPLOI'
  | 'CEJ_CONTRAT_ENGAGEMENT_JEUNE'
  | 'CUI_CONTRAT_UNIQUE_INSERTION'
  | 'PUBLIC_ASSISTANCE'
  | 'RSA_REVENU_DE_SOLIDARITE_ACTIVE';

interface AllowancesCountries {
  iso2Code: string;
  name: string;
}

export interface AdministrativeDataFormat {
  id: number;
  countryIso2Code: string;
  datafield: string;
  pcreRegex: string;
  description: string;
}

export interface UserAllowances {
  id: number;
  allowance: {
    id: number;
    description: string;
    code: string;
  };
  value: number;
  createdAt: string;
  updatedAt: string;
}

export interface FundingRequest {
  id: string;
  type: string;
  status: string;
  requestedPathId: number;
  availability: string;
  fundingBodyFileId: string;
  fundingBodyUserEmail: string;
  fundingBodyStatus: string;
  fundingBodyThirdPartyContributionStatus: string;
}

export interface JobAutoApplySettings {
  opportunityTypes: OpportunityType[];
  motivation: string;
}

export interface SessionTerms {
  duration: string;
  pace: string;
  sessionsPerRecurrence: number;
  updatedAt: string;
}

export interface StudentPathDossier {
  id: number;
  doNotAssignMentorDate: string;
}

export interface TrainingProgramEligibility {
  criteria: string;
  scoring: number;
  tests: {
    inputData: string;
    expectedOutput: { status: 'eligible' | 'not-eligible'; 'derogatory-process': boolean };
  }[];
}

export interface UserInterest {
  type: 'category' | 'path';
  id: number;
}

export interface UserOngoingPath {
  id: number;
  title: string;
  certificationLevel: string;
  category: PathCategory & {
    academy?: {
      name: string;
    };
  };
  followedAt: string;
  unfollowedAt: string;
  toolsAndMethodologies: string[];
  objectives: string[];
}

interface JurySession {
  id: number;
  jurySessionDate: string;
  jurySignatory: string | null;
  learningPath: {
    id: number;
    locale: string;
    title: string;
  };
  pvSignedAt: string | null;
  graduationRate: number | null;
}

export interface EligibleStudent {
  student: {
    id: number;
    displayName: string;
    email: string;
  };
  status: 'eligible' | 'not-eligible';
}

export interface StudentCredential {
  id: number;
  certification: {
    holderEnvironment: 'internal' | 'external';
    category: CertificationCategory;
  };
  credentialNumber: string;
  jurySession: JurySession;
}

export enum LifecycleStatus {
  ReadyToGraduate = 'ready-to-graduate',
  NotValidated = 'not-validated',
  Graduated = 'graduated',
  InProgress = 'in-progress',
  WaitingForAgreement = 'waiting-for-agreement',
  Validated = 'validated',
}

export interface JurySessionAssessment {
  id: number;
  jurySessionId: number;
  lifeCycleStatus: LifecycleStatus;
  ineligibleJuries: number[];
  results: Array<{ juryMember: { id: number } }>;
  jurySession: {
    id: number;
    jurySessionDate: string;
  };
  student: {
    id: number;
    displayName: string;
  };
  studentCredential: StudentCredential | null;
}

export * from './modules/helpers/modules/assessment/types';
export * from './modules/helpers/modules/user/types';
export * from './modules/helpers/modules/talent/types';
export * from './modules/helpers/modules/company/types';
export * from './modules/helpers/modules/learning-content/types';
export * from './modules/helpers/modules/community-paths/types';
export * from './modules/helpers/modules/communication/types';
export * from './modules/helpers/modules/talents-saved-searches/types';
export * from './modules/helpers/modules/reference-skills/types';
export * from './modules/helpers/modules/public-applications/types';
